import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Divider, Typography, Tooltip, IconButton, Grid } from '@mui/material'
import { DateRangePicker } from 'frame/components'
import { hasRoles } from 'acl'

import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

const DateRangeValueText = ({
  disabled,
  userRoles,
  format,
  label,
  startValue,
  endValue,
  startMinDate,
  endMaxDate,
  onCancel,
  onDateRangeChange,
  onDateRangeSaveChanges,
  toggleEdit, // to toggle edit mode from outside this valueText component
  onEdit, // custom function passed to call on editmode select
}) => {

  const [editMode, setEditMode] = useState(false)
  const [showIsRequiredError, setShowIsRequiredError] = useState(false)
  const [inputValue, setInputValue] = useState([startValue || null , endValue || null]) // to keep track of value change locally for required field validation

  useEffect(() => {
    if (toggleEdit) {
      setEditMode(false)
      setShowIsRequiredError(false)
    }
  }, [toggleEdit])

  useEffect(() => {
    const requiredRule = inputValue && ((inputValue[0] === null || inputValue[0] === '') || (inputValue[1] === null || inputValue[1] === ''))
    setShowIsRequiredError(requiredRule)
    // eslint-disable-next-line
  }, [inputValue])

  const hasAccess = ! hasRoles(['fleetcoordinator'], userRoles)
  const editable = !disabled && hasAccess && onDateRangeChange

  const onSaveHandler = () => {
    !editMode && setEditMode(true)
    if (editMode && moment(startValue) < moment(endValue)) {

      onDateRangeSaveChanges && onDateRangeSaveChanges(startValue, endValue)
      // console.log('value text onSaveHandler ', { startDate: moment(startValue).format('DD/MM/YY'), endDate: moment(endValue).format('DD/MM/YY') })
      setEditMode(! editMode)
    }
    onEdit && onEdit() // call custom onEdit() function if onEdit prop exists
  }

  const onClearHandler = () => {
    setShowIsRequiredError(false)
    onCancel()
    setEditMode(! editMode)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          position: 'relative',
          padding: 1,
          marginBottom: -2,
        }}>
        <Grid container spacing={1}>
          <Grid item md={2} sm={3} xs={12}>
            <Typography
              sx={{
                paddingRight: 1,
                minWidth: '150px',
                fontWeight: '500',
              }}
              variant="body1">
              {label}
            </Typography>
          </Grid>

          <Grid item md={9} sm={8} xs={12}>

            {editMode ? (
              <div>
                <DateRangePicker
                  tight allowSameDateSelection
                  format={format}
                  values={[startValue, endValue]}
                  startDateInputSize={280}
                  startMinDate={startMinDate}
                  endMaxDate={endMaxDate}
                  endDateInputSize={280}
                  onDateRangeChange={(startDate, endDate) => {
                    onDateRangeChange(startDate, endDate)
                    setInputValue([startDate, endDate])
                  }}
                  showStartDateRequiredError={showIsRequiredError && inputValue && (inputValue[0] === null || inputValue[0] === '')}
                  showEndDateRequiredError={showIsRequiredError && inputValue && (inputValue[1] === null || inputValue[1] === '')}
                  placeholderText="DD/MM/YYYY"/>
              </div>
            ) : (
              <Typography variant="body1" align="left">
                {moment(startValue).format('Do MMMM YYYY') || '-'}
                <span style={{ padding: '0 10px' }}>
                  {' to '}
                </span>
                {moment(endValue).format('Do MMMM YYYY') || '-'}
              </Typography>
            )}

            {editable && (
              <div
                style={{
                  display: 'flex',
                  width: '80px',
                  height: '20px',
                }}/>
            )}

          </Grid>
        </Grid>

        <div
          style={{
            marginBottom: '0',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'absolute',
            top: showIsRequiredError ? -23 : -10,
            right: 0,
            height: '100%',
            '@media (max-width: 0px)': {
              top: -30,
            },
          }}>
          {editable && (
            <Tooltip
              sx={{
                marginLeft: 1,
              }}
              title={editMode ? 'Save' : 'Edit'}>
              <IconButton size="small" onClick={onSaveHandler} disabled={showIsRequiredError}>
                {editMode ? (
                  <SaveIcon
                    sx={{
                      width: 20,
                      height: 20,
                      '@media (max-width: 0px)': {
                        marginTop: -5,
                      }
                    }}/>
                ) : (
                  <EditIcon
                    sx={{
                      width: 20,
                      height: 20,
                    }}/>
                )}
              </IconButton>
            </Tooltip>
          )}
          {editMode && (
            <Tooltip
              sx={{
                marginLeft: 1,
              }}
              title="Cancel">
              <IconButton
                sx={{
                  '@media (max-width: 0px)': {
                    marginTop: -5,
                  }
                }}
                size="small" onClick={onClearHandler}>
                <ClearIcon
                  sx={{
                    width: 20,
                    height: 20,
                  }}/>
              </IconButton>
            </Tooltip>
          )}
        </div>

      </div>
      <Divider />
    </div>
  )
}

DateRangeValueText.propTypes = {
  disabled: PropTypes.bool,
  userRoles: PropTypes.array.isRequired,
  format: PropTypes.string,
  label: PropTypes.string.isRequired,
  startValue: PropTypes.string,
  startMinDate: PropTypes.any,
  endValue: PropTypes.string,
  endMaxDate: PropTypes.any,
  onCancel: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  onDateRangeSaveChanges: PropTypes.func,
  toggleEdit: PropTypes.bool, // to toggle edit mode from outside this valueText component
  onEdit: PropTypes.func, // custom function passed to call on editmode select
}

const mapStateToProps = ({ settings }) => ({
  userRoles: settings.roles,
})

const mapDispatchToProps = {}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DateRangeValueText)
